import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, Optional, ViewChild } from "@angular/core";
import { SafeResourceUrl } from "@angular/platform-browser";
import { CommonModule } from "@angular/common";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatTableModule } from "@angular/material/table";
import { select, Store } from "@ngrx/store";
import { combineLatest, map, Observable, Subscription } from "rxjs";
import { Webviewer } from "libs/treatments/src/lib/models/treatment.model";
import { FormsModule } from "@angular/forms";
import { TreatmentBaseService } from "libs/treatments/src/lib/services/treatment.base.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MatButtonModule } from "@angular/material/button";
import { ClipboardModule } from 'ngx-clipboard';
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MediaService } from "libs/media/src/lib/services/media.service";
import { MediaModel, selectMediaById } from "libs/media/src/lib/store";
import { MessageModel, MessageScopes, MessageTypeIcons, MessageTypes, SendersTypes } from "libs/messages/src/lib/models";
import { selectMessageById } from "libs/messages/src/lib/store/messages.selectors";
import { TmCommonModule } from "libs/tm-common/src/lib/tm-common.module";

const DATE_FORMAT_DE = "dd.MM.yy HH:mm";
const DATE_FORMAT_EN = "dd/MM/yy hh:mm a";

const CA_WEBVIEWER_APP_URL = 'https://' + window.location.host + '/caWebViewer/index.html';

@Component({
	selector: "ux-webviewer-preview",
	standalone: true,
	imports: [
		CommonModule,
		MatButtonModule,
		ClipboardModule,
		MatCardModule,
		MatFormFieldModule,
		MatSelectModule,
		MatTableModule,
		FormsModule,
		TranslateModule,
		TmCommonModule
	],
	templateUrl: "./webviewer-preview.component.html",
	styleUrls: ["./webviewer-preview.component.scss"]
})
export class WebviewerPreviewComponent implements OnInit, OnDestroy {
	SendersTypes = SendersTypes;
	MessageTypeIcons = MessageTypeIcons;
	MessageScopes = MessageScopes;
	MessageTypes = MessageTypes;

	@Input() webviewers: Webviewer[] = [];
	@Input() selectedWebviewer: Webviewer = null;

	@ViewChild('webviewerIframe') webviewerIframe: ElementRef;

	protected dateFormat = DATE_FORMAT_DE;

	sanitizedAppUrl: SafeResourceUrl;

	relatedMessages$: Observable<MessageModel[]> = null;
	relatedMedia$: Observable<MediaModel[]> = null;

	private translationSubscription: Subscription;

	constructor(
		private treatmentBaseService: TreatmentBaseService,
		private mediaService: MediaService,
		private store: Store,
		private translate: TranslateService,
		@Optional() @Inject(MAT_DIALOG_DATA) public data: { webviewers: Webviewer[], selectedWebviewer?: Webviewer },
		@Inject('CONFIG') private config
	) {
		if (data) {
			this.webviewers = data.webviewers;
			if (data.selectedWebviewer) {
				this.selectedWebviewer = data.selectedWebviewer;
			}
		}
	}

	ngOnInit() {
		// sanitize app url to bypass security restrictions
		this.sanitizedAppUrl = this.treatmentBaseService.getCaWebViewerSource(CA_WEBVIEWER_APP_URL);
		this.selectedWebviewer ??= this.webviewers.at(-1);
		const mediaId = this.getMediaIdFromUrl(this.selectedWebviewer.url);
		this.getWebviewerFile(mediaId);
		this.fetchRelatedMediaAndMessages(this.selectedWebviewer);

		this.dateFormat = this.translate.currentLang === "de" ? DATE_FORMAT_DE : DATE_FORMAT_EN;
		this.translationSubscription = this.translate.onLangChange.subscribe(evt => {
			this.dateFormat = evt.lang === "de" ? DATE_FORMAT_DE : DATE_FORMAT_EN;
		});
	}

	ngOnDestroy(): void {
		this.translationSubscription?.unsubscribe();
	}

	onSelectionChange(value: Webviewer) {
		//reset webviewer link to refresh iframe to prevent multiple 3d models in one viewer
		this.sanitizedAppUrl = "";
		this.sanitizedAppUrl = this.treatmentBaseService.getCaWebViewerSource(CA_WEBVIEWER_APP_URL);

		const mediaId = this.getMediaIdFromUrl(value.url);
		this.getWebviewerFile(mediaId);
		this.fetchRelatedMediaAndMessages(this.selectedWebviewer);
	}

	private fetchRelatedMediaAndMessages(webviewer: Webviewer): void {
		this.relatedMessages$ = combineLatest(
			(webviewer.relatedMessages ?? []).map(messageId =>
				this.store.pipe(select(selectMessageById(messageId))))
		).pipe(map(messages => messages.filter(message => !!message)));

		this.relatedMedia$ = combineLatest(
			(webviewer.relatedMedia ?? []).map(mediaId =>
				this.store.pipe(select(selectMediaById(mediaId))))
		).pipe(map(media => media.filter(media => !!media)));
	}

	getMediaIdFromUrl(url: string): string{
		const urlParts = url.split('/');
		return urlParts[urlParts.length - 1]; //media id of .json file
	}

	getWebviewerFile(mediaId: string) {
		this.mediaService.getFileAsObject(mediaId)
			.subscribe(webViewerInput => {
				// post data to iframe
				if (this.webviewerIframe?.nativeElement && webViewerInput) {
					setTimeout(() => {
						const contentWindow = this.webviewerIframe.nativeElement.contentWindow;
						contentWindow.postMessage({language: this.config.locale}, CA_WEBVIEWER_APP_URL);
						contentWindow.postMessage(webViewerInput, CA_WEBVIEWER_APP_URL);
					}, 1000);
				}
			});
	}

	//helper function to determine a default value for the mat-select component, since
	//angular < 16 is not capable of doing it out of the box for non primitve values (resp. objects)
	compareWebviewers(viewer1: any, viewer2: any): boolean {
		return viewer1.name === viewer2.name && viewer1.url === viewer2.url;
	}

	protected isStaffMemberMessage(message: MessageModel) {
		return message.sendersType === SendersTypes.Support
			|| message.sendersType === SendersTypes.Technican
			|| message.sendersType === SendersTypes.System;
	}

	protected sendersTypeLabel(type: SendersTypes): string {
		switch (type) {
			case SendersTypes.Customer:
				return "DOC";
			case SendersTypes.System:
				return "SYS";
			case SendersTypes.Technican:
				return "TEC";
			case SendersTypes.Support:
				return "SUP";
			default:
				return "???";
		}
	}

	getFileSizeKb(bytes: number) {
		return Math.ceil(bytes / 1024).toLocaleString(this.config.locale);
	}

	protected downloadMediaFile(mediaId: string) {
		this.mediaService.getFile(mediaId);
	}
}
